import { useCallback, useEffect, useState } from "react";
import { CalendarClock, AlertOctagon, Users, Wrench, SlidersHorizontal } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { AssignedToMe } from "@/components/dashboard/AssignedToMe";
import { useAuth } from "@/context/AuthContext";
import { fmtDay, greeting } from "@/lib/format";
import { DemoBadge, PageHeader } from "@/components/common/ui-kit";
import { Skeleton } from "@/components/ui/skeleton";
import { TodayStrip } from "@/components/dashboard/TodayStrip";
import { TechTodayCard } from "@/components/dashboard/TechTodayCard";
import { AttentionPanel } from "@/components/dashboard/AttentionPanel";
import { UpcomingTimeline } from "@/components/dashboard/UpcomingTimeline";
import { CommercialCard, CrmCard, OperationsCard } from "@/components/dashboard/AreaCards";
import { PeriodFilter, usePeriod } from "@/components/common/PeriodFilter";
import { DashboardCustomize } from "@/components/dashboard/DashboardCustomize";
import { TechReportsCard } from "@/components/dashboard/TechReportsCard";
import { Button } from "@/components/ui/button";

const MAIN = ["tech_today", "today", "attention", "upcoming"];

export const DASH_ICONS = { CalendarClock, AlertOctagon, Users, Wrench };

const Loading = () => (
  <div className="space-y-6" data-testid="dashboard-loading">
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-card" />)}</div>
    <Skeleton className="h-72 rounded-card" />
  </div>
);

export default function Dashboard() {
  const { me, can } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = usePeriod("onsuite.dashboard.period");
  const [layout, setLayout] = useState(null), [custom, setCustom] = useState(false);
  const loadLayout = useCallback(() => api.get("/dashboard/layout").then((r) => setLayout(r.data.sections)).catch(() => setLayout([])), []);
  useEffect(() => { loadLayout(); }, [loadLayout]);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/dashboard/summary", { params: { period } }).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errorMessage(e)));
  }, [period, tick]);

  const blocks = data ? {
    tech_today: <TechTodayCard key="tech_today" />, today: data.today && <TodayStrip key="today" today={data.today} />,
    tech_reports: data.tech_reports && <TechReportsCard key="tech_reports" data={data.tech_reports} />,
    attention: <AttentionPanel key="attention" items={data.attention} />, upcoming: <UpcomingTimeline key="upcoming" items={data.upcoming} onDone={() => setTick((t) => t + 1)} />,
    crm: data.crm && <CrmCard key="crm" data={data.crm} period={data.period} />,
    commercial: data.commercial && <CommercialCard key="commercial" data={data.commercial} period={data.period} />,
    operations: data.operations && <OperationsCard key="operations" data={data.operations} period={data.period} />,
  } : {};
  const available = new Set(Object.keys(blocks).filter((k) => blocks[k] && (k !== "tech_today" || can("interventions:execute"))));
  const shown = (layout || []).filter((s) => s.visible && available.has(s.key)).map((s) => s.key);
  const main = shown.filter((k) => MAIN.includes(k)), side = shown.filter((k) => !MAIN.includes(k));

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        overline={fmtDay(new Date())}
        title={me.user.first_name ? `${greeting()}, ${me.user.first_name}` : greeting()}
        description="Ecco la situazione di oggi, cosa richiede attenzione e cosa è pianificato."
        actions={<div className="flex flex-wrap items-center gap-3">{data?.data_mode === "demo" && <DemoBadge data-testid="dashboard-demo-mode-badge" />}
          <PeriodFilter value={period} onChange={setPeriod} testId="dashboard-period" />
          <Button variant="outline" className="gap-2" onClick={() => setCustom(true)} disabled={!data} data-testid="dashboard-customize-btn"><SlidersHorizontal className="h-4 w-4" />Personalizza</Button></div>}
      />
      {data?.data_mode === "demo" && (
        <p className="-mt-4 mb-6 text-xs text-slate-500" data-testid="dashboard-demo-notice">
          Modalità dati dimostrativi attiva: i valori mostrati sono a scopo illustrativo e separati dai dati reali.
        </p>
      )}
      {error && <p className="rounded-control border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert" data-testid="dashboard-error">{error}</p>}
      <AssignedToMe />
      {(!data || !layout) && !error && <Loading />}
      {data && layout && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12" data-testid="dashboard-sections">
          {main.length > 0 && <div className={`flex flex-col gap-6 ${side.length ? "lg:col-span-8" : "lg:col-span-12"}`}>{main.map((k) => blocks[k])}</div>}
          {side.length > 0 && <div className={`flex flex-col gap-6 ${main.length ? "lg:col-span-4" : "lg:col-span-12"}`}>{side.map((k) => blocks[k])}</div>}
          {!shown.length && <p className="rounded-control border border-dashed border-line p-8 text-center text-sm text-slate-500 lg:col-span-12" data-testid="dashboard-empty-layout">Nessuna sezione visibile. Usa "Personalizza" per sceglierle.</p>}
        </div>
      )}
      <DashboardCustomize open={custom} onOpenChange={setCustom} available={available} onSaved={loadLayout} />
    </div>
  );
}
